"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AppLogo from "@/components/ui/AppLogo";

interface FieldErrors {
  email?: string;
  password?: string;
}

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className="fixed top-6 right-6 z-50 flex items-center gap-3 bg-emerald-900/90 border border-emerald-500/40 text-emerald-300 text-sm px-5 py-3.5 rounded-lg shadow-xl backdrop-blur-md">
      <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 text-emerald-400/60 hover:text-emerald-300 transition-colors">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default function LoginPage() {
  const router = useRouter();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "email":
        if (!value.trim()) return "Email address is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email address.";
        return "";
      case "password":
        if (!value) return "Password is required.";
        if (value.length < 6) return "Password must be at least 6 characters.";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    const errors: FieldErrors = {
      email: validateField("email", formData.email),
      password: validateField("password", formData.password),
    };
    setFieldErrors(errors);
    if (Object.values(errors).some((err) => err)) return;

    setLoading(true);
    setTimeout(() => {
      const user = {
        fullName: formData.email.split("@")[0],
        email: formData.email,
        company: "",
      };
      sessionStorage.setItem("standave_user", JSON.stringify(user));
      sessionStorage.setItem("standave_auth", "true");
      setLoading(false);
      setToast("Sign in successful! Redirecting to dashboard\u2026");
      setTimeout(() => router.push("/dashboard"), 1500);
    }, 1000);
  };

  const inputClass = (field: keyof FieldErrors) =>
    `w-full bg-navy/60 border text-cream placeholder-cream/30 px-4 py-3 rounded-sm text-sm focus:outline-none transition-colors ${
      touched[field] && fieldErrors[field]
        ? "border-red-500/60 focus:border-red-500" :"border-[rgba(201,168,76,0.2)] focus:border-gold/60"
    }`;

  return (
    <main className="bg-navy min-h-screen">
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
      <Header />
      <section className="min-h-screen flex items-center justify-center px-4 pt-28 pb-16">
        <div className="w-full max-w-md">
          <div className="bg-[#0C1B33]/80 backdrop-blur-xl border border-[rgba(201,168,76,0.18)] rounded-sm p-8 shadow-[0_8px_60px_rgba(0,0,0,0.5)]">
            <div className="flex justify-center mb-6">
              <AppLogo size={40} text="StanDave" iconName="BuildingOffice2Icon" className="text-cream" />
            </div>
            <h1 className="font-display text-2xl text-cream text-center mb-1">Welcome Back</h1>
            <p className="text-cream/50 text-sm text-center mb-8">Sign in to access your StanDave dashboard</p>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label className="block text-cream/70 text-xs font-display uppercase tracking-widest mb-1.5">
                  Email Address <span className="text-gold">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="john@company.com"
                  className={inputClass("email")}
                />
                {touched.email && fieldErrors.email && (
                  <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-cream/70 text-xs font-display uppercase tracking-widest mb-1.5">
                  Password <span className="text-gold">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your password"
                  className={inputClass("password")}
                />
                {touched.password && fieldErrors.password && (
                  <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {fieldErrors.password}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3.5 text-sm font-display uppercase tracking-widest rounded-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </form>

            <p className="text-cream/40 text-sm text-center mt-6">
              Don&apos;t have an account?{" "}
              <Link href="/signup" className="text-gold hover:text-gold/80 transition-colors">
                Create Account
              </Link>
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
